import { useState } from 'react';
import { RolePermissionsManager } from '@/components/settings/RolePermissionsManager';
import { CustomRoleManager } from '@/components/settings/CustomRoleManager';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, localAuthApi, userSyncApi, rolesApi } from '@/lib/api';
import type { LocalUserInfo, UserRole } from 'shared/types';
import type {
  OpUserInfo,
  UserSyncMappingInfo,
  CustomRoleInfo,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Trash2,
  User as UserIcon,
  Shield,
  Edit,
  KeyRound,
  Link2,
  Unlink,
  Crown,
  Code,
  Briefcase,
  Eye,
} from 'lucide-react';

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'developer', label: 'Developer' },
  { value: 'ba', label: 'BA' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];

const ROLE_CONFIG: Record<
  string,
  { label: string; icon: typeof Shield; color: string; bg: string }
> = {
  admin: {
    label: 'Admin',
    icon: Crown,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
  },
  developer: {
    label: 'Developer',
    icon: Code,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  ba: {
    label: 'BA',
    icon: Briefcase,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  member: {
    label: 'Member',
    icon: UserIcon,
    color: 'text-slate-400',
    bg: 'bg-slate-500/10',
  },
  viewer: {
    label: 'Viewer',
    icon: Eye,
    color: 'text-gray-400',
    bg: 'bg-gray-500/10',
  },
};

function RoleBadge({
  role,
  customRole,
}: {
  role: string;
  customRole?: CustomRoleInfo | null;
}) {
  if (customRole) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{
          color: customRole.color,
          backgroundColor: `${customRole.color}18`,
        }}
      >
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: customRole.color }}
        />
        {customRole.display_name}
      </span>
    );
  }
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color} ${cfg.bg}`}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

export function UsersSettings() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] =
    useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<LocalUserInfo | null>(null);
  const [selectedOpUserId, setSelectedOpUserId] = useState<string>('');
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'developer' as UserRole,
    custom_role_id: null as number | null,
  });
  const [editUser, setEditUser] = useState({
    username: '',
    role: 'developer' as UserRole,
    custom_role_id: null as number | null,
  });
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  const { data: currentUser } = useQuery({
    queryKey: ['localUser'],
    queryFn: localAuthApi.getCurrentUser,
  });

  // OP users for link dropdown
  const { data: opUsers = [], isLoading: opUsersLoading } = useQuery({
    queryKey: ['opUsers'],
    queryFn: userSyncApi.listOpUsers,
    enabled: currentUser?.role === 'admin',
  });

  // User sync mappings
  const { data: syncMappings = [], refetch: refetchMappings } = useQuery({
    queryKey: ['userSyncMappings'],
    queryFn: userSyncApi.getMappings,
    enabled: currentUser?.role === 'admin',
  });

  // Roles & permissions data for custom roles
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesApi.getRoles,
    enabled: currentUser?.role === 'admin',
  });

  const customRoles = rolesData?.custom_roles ?? [];
  const getCustomRoleById = (
    id: number | null | undefined
  ): CustomRoleInfo | null =>
    id ? (customRoles.find((r) => r.id === id) ?? null) : null;

  const createUserMutation = useMutation({
    mutationFn: async (data: {
      username: string;
      password: string;
      role: UserRole;
      custom_role_id: number | null;
    }) => {
      // Step 1: Create user with built-in role (API doesn't accept custom_role_id on create)
      const { custom_role_id, ...createData } = data;
      const createdUser = await usersApi.create(createData);

      // Step 2: If custom role selected, immediately assign it
      if (custom_role_id && createdUser.id) {
        await usersApi.update(Number(createdUser.id), { custom_role_id });
      }

      return createdUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsCreateDialogOpen(false);
      setNewUser({
        username: '',
        password: '',
        role: 'developer',
        custom_role_id: null,
      });
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: { username?: string; role?: UserRole; custom_role_id?: number };
    }) => usersApi.update(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, password }: { userId: number; password: string }) =>
      usersApi.resetPassword(userId, password),
    onSuccess: () => {
      setIsResetPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Link / Unlink mutations
  const linkMutation = useMutation({
    mutationFn: ({
      userId,
      opUserId,
      opEmail,
      opName,
    }: {
      userId: number;
      opUserId: number;
      opEmail: string;
      opName: string;
    }) => userSyncApi.link(userId, opUserId, opEmail, opName),
    onSuccess: () => {
      refetchMappings();
      setIsLinkDialogOpen(false);
      setSelectedUser(null);
      setSelectedOpUserId('');
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (userId: number) => userSyncApi.unlink(userId),
    onSuccess: () => {
      refetchMappings();
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Helpers
  const getMappingForUser = (userId: number): UserSyncMappingInfo | undefined =>
    syncMappings.find((m) => m.pai_user_id === userId);

  const getOpUserDisplay = (opUser: OpUserInfo): string => {
    const name =
      opUser.name ||
      [opUser.first_name, opUser.last_name].filter(Boolean).join(' ');
    return name ? `${name} (${opUser.email})` : opUser.email || opUser.login;
  };

  // Already-linked OP user IDs (to exclude from dropdown)
  const linkedOpUserIds = new Set(syncMappings.map((m) => m.op_user_id));

  const handleCreateUser = () => {
    if (!newUser.username || !newUser.password) {
      setError('Username and password are required.');
      return;
    }
    setError(null);
    createUserMutation.mutate(newUser);
  };

  const handleDeleteUser = (userId: number) => {
    if (confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleOpenEditDialog = (user: LocalUserInfo) => {
    setSelectedUser(user);
    setEditUser({
      username: user.username,
      role: user.role,
      custom_role_id: Number(user.custom_role_id) ?? null,
    });
    setError(null);
    setIsEditDialogOpen(true);
  };

  const handleUpdateUser = () => {
    if (!selectedUser || !editUser.username) {
      setError('Username is required.');
      return;
    }
    setError(null);
    const data: {
      username?: string;
      role?: UserRole;
      custom_role_id?: number;
    } = {
      username: editUser.username,
    };
    if (editUser.custom_role_id) {
      // Assigning a custom role
      data.custom_role_id = editUser.custom_role_id;
    } else {
      // Using built-in role
      data.role = editUser.role;
    }
    updateUserMutation.mutate({
      userId: Number(selectedUser.id),
      data,
    });
  };

  const handleOpenResetPasswordDialog = (user: LocalUserInfo) => {
    setSelectedUser(user);
    setNewPassword('');
    setError(null);
    setIsResetPasswordDialogOpen(true);
  };

  const handleResetPassword = () => {
    if (!selectedUser || !newPassword) {
      setError('Password is required.');
      return;
    }
    setError(null);
    resetPasswordMutation.mutate({
      userId: Number(selectedUser.id),
      password: newPassword,
    });
  };

  const handleOpenLinkDialog = (user: LocalUserInfo) => {
    setSelectedUser(user);
    setSelectedOpUserId('');
    setError(null);
    setIsLinkDialogOpen(true);
  };

  const handleLinkUser = () => {
    if (!selectedUser || !selectedOpUserId) {
      setError('Please select an OpenProject user.');
      return;
    }
    const opUser = opUsers.find((u) => u.id === parseInt(selectedOpUserId));
    if (!opUser) {
      setError('Selected user not found.');
      return;
    }
    setError(null);
    linkMutation.mutate({
      userId: Number(selectedUser.id),
      opUserId: opUser.id,
      opEmail: opUser.email || '',
      opName:
        opUser.name ||
        [opUser.first_name, opUser.last_name].filter(Boolean).join(' ') ||
        opUser.login,
    });
  };

  const handleUnlinkUser = (userId: number) => {
    if (
      confirm('Are you sure you want to unlink this user from OpenProject?')
    ) {
      unlinkMutation.mutate(userId);
    }
  };

  const canManageUsers = currentUser?.role === 'admin';
  if (!currentUser || !canManageUsers) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          You do not have permission to access this page.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">User Management</h2>
          <p className="text-muted-foreground">
            Manage user accounts and their roles.
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Username</TableHeaderCell>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell className="text-right">Actions</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.map((user) => {
              const mapping = getMappingForUser(Number(user.id));
              return (
                <TableRow key={user.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    {user.username}
                  </TableCell>
                  <TableCell>
                    <RoleBadge
                      role={user.role}
                      customRole={getCustomRoleById(
                        Number(user.custom_role_id)
                      )}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEditDialog(user)}
                        title="Edit user"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenResetPasswordDialog(user)}
                        title="Reset password"
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      {mapping ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleUnlinkUser(Number(user.id))}
                          title={`Unlink from OpenProject (${mapping.op_email})`}
                        >
                          <Unlink className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenLinkDialog(user)}
                          disabled={opUsersLoading}
                          title="Link to OpenProject"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(Number(user.id))}
                        disabled={user.id === currentUser.id}
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account with the specified username, password,
              and role.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
              {error}
            </div>
          )}
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={newUser.username}
                onChange={(e) =>
                  setNewUser({ ...newUser, username: e.target.value })
                }
                placeholder="Enter username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
                placeholder="Enter password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={
                  newUser.custom_role_id
                    ? `custom:${newUser.custom_role_id}`
                    : newUser.role
                }
                onValueChange={(value: string) => {
                  if (value.startsWith('custom:')) {
                    const id = parseInt(value.split(':')[1]);
                    setNewUser({
                      ...newUser,
                      custom_role_id: id,
                      role: 'member' as UserRole,
                    });
                  } else {
                    setNewUser({
                      ...newUser,
                      role: value as UserRole,
                      custom_role_id: null,
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                  {customRoles.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-1">
                        Custom Roles
                      </div>
                      {customRoles.map((cr) => (
                        <SelectItem
                          key={`custom:${cr.id}`}
                          value={`custom:${cr.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: cr.color }}
                            />
                            {cr.display_name}
                          </span>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={createUserMutation.isPending}
            >
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update the username or role for this user.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
              {error}
            </div>
          )}
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-username">Username</Label>
              <Input
                id="edit-username"
                value={editUser.username}
                onChange={(e) =>
                  setEditUser({ ...editUser, username: e.target.value })
                }
                placeholder="Enter username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select
                value={
                  editUser.custom_role_id
                    ? `custom:${editUser.custom_role_id}`
                    : editUser.role
                }
                onValueChange={(value: string) => {
                  if (value.startsWith('custom:')) {
                    const id = parseInt(value.split(':')[1]);
                    setEditUser({
                      ...editUser,
                      custom_role_id: id,
                      role: 'member' as UserRole,
                    });
                  } else {
                    setEditUser({
                      ...editUser,
                      role: value as UserRole,
                      custom_role_id: null,
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                  {customRoles.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-1">
                        Custom Roles
                      </div>
                      {customRoles.map((cr) => (
                        <SelectItem
                          key={`custom:${cr.id}`}
                          value={`custom:${cr.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: cr.color }}
                            />
                            {cr.display_name}
                          </span>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateUser}
              disabled={updateUserMutation.isPending}
            >
              {updateUserMutation.isPending ? 'Updating...' : 'Update User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={isResetPasswordDialogOpen}
        onOpenChange={setIsResetPasswordDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.username}.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
              {error}
            </div>
          )}
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsResetPasswordDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending
                ? 'Resetting...'
                : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to OpenProject Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to OpenProject User</DialogTitle>
            <DialogDescription>
              Select an OpenProject user to link with{' '}
              <strong>{selectedUser?.username}</strong>. This will sync the PAI
              user ID and username to OpenProject.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
              {error}
            </div>
          )}
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="op-user-select">OpenProject User</Label>
              <Select
                value={selectedOpUserId}
                onValueChange={setSelectedOpUserId}
              >
                <SelectTrigger id="op-user-select">
                  <SelectValue placeholder="Select an OpenProject user..." />
                </SelectTrigger>
                <SelectContent>
                  {opUsers
                    .filter((u) => !linkedOpUserIds.has(u.id))
                    .map((opUser) => (
                      <SelectItem key={opUser.id} value={opUser.id.toString()}>
                        {getOpUserDisplay(opUser)}
                      </SelectItem>
                    ))}
                  {opUsers.filter((u) => !linkedOpUserIds.has(u.id)).length ===
                    0 && (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No available OpenProject users
                    </div>
                  )}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Only users not already linked are shown. Make sure OpenProject
                sync is enabled in settings.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsLinkDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkUser}
              disabled={linkMutation.isPending || !selectedOpUserId}
            >
              {linkMutation.isPending ? 'Linking...' : 'Link Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permission Matrix */}
      <div className="border-t border-border pt-6 mt-6">
        <RolePermissionsManager />

        {/* Custom Roles */}
        {rolesData && (
          <CustomRoleManager
            allPermissions={rolesData.all_permissions}
            customRoles={rolesData.custom_roles}
            isOwner={currentUser?.role === 'admin'}
          />
        )}
      </div>
    </div>
  );
}
